import { formatChord, parseChord, type ParsedChord } from './chord'
import { pitchClass } from './notes'

/**
 * Catálogo propio de digitaciones (§3.3). Cada forma se comprueba en las pruebas: sus notas
 * tienen que pertenecer al acorde, incluir la fundamental y la tercera (o la cuarta en «sus») y,
 * si hay bajo alternativo, sonar como nota más grave.
 *
 * Cuerdas de la 6.ª (Mi grave) a la 1.ª (Mi agudo). «x» = no se toca, 0 = al aire.
 */
export type Fret = number | 'x'

export interface Voicing {
  frets: readonly [Fret, Fret, Fret, Fret, Fret, Fret]
  /** Cejilla: traste y cuerdas que pisa (de la más grave a la más aguda, 6 → 1). */
  barre?: { fret: number; fromString: number; toString: number }
}

/** Afinación estándar en clases de altura, de la 6.ª a la 1.ª cuerda. */
export const STANDARD_TUNING = [4, 9, 2, 7, 11, 4] as const

const CATALOG: Record<string, readonly Voicing[]> = {
  C: [{ frets: ['x', 3, 2, 0, 1, 0] }],
  'C/E': [{ frets: [0, 3, 2, 0, 1, 0] }],
  C7: [{ frets: ['x', 3, 2, 3, 1, 0] }],
  Cmaj7: [{ frets: ['x', 3, 2, 0, 0, 0] }],
  D: [{ frets: ['x', 'x', 0, 2, 3, 2] }],
  'D/F#': [{ frets: [2, 'x', 0, 2, 3, 2] }],
  D7: [{ frets: ['x', 'x', 0, 2, 1, 2] }],
  Dmaj7: [{ frets: ['x', 'x', 0, 2, 2, 2] }],
  Dsus4: [{ frets: ['x', 'x', 0, 2, 3, 3] }],
  Dadd9: [{ frets: ['x', 5, 4, 2, 3, 0] }],
  Dm: [{ frets: ['x', 'x', 0, 2, 3, 1] }],
  Dm7: [{ frets: ['x', 'x', 0, 2, 1, 1] }],
  E: [{ frets: [0, 2, 2, 1, 0, 0] }],
  E7: [{ frets: [0, 2, 0, 1, 0, 0] }, { frets: [0, 2, 2, 1, 3, 0] }],
  Esus4: [{ frets: [0, 2, 2, 2, 0, 0] }],
  Em: [{ frets: [0, 2, 2, 0, 0, 0] }],
  Em7: [{ frets: [0, 2, 2, 0, 3, 0] }],
  F: [{ frets: [1, 3, 3, 2, 1, 1], barre: { fret: 1, fromString: 6, toString: 1 } }],
  Fmaj7: [{ frets: ['x', 'x', 3, 2, 1, 0] }],
  'F#m7b5': [{ frets: [2, 'x', 2, 2, 1, 'x'] }],
  G: [{ frets: [3, 2, 0, 0, 0, 3] }, { frets: [3, 2, 0, 0, 3, 3] }],
  G7: [{ frets: [3, 2, 0, 0, 0, 1] }],
  Gmaj7: [{ frets: [3, 2, 0, 0, 0, 2] }],
  A: [{ frets: ['x', 0, 2, 2, 2, 0] }],
  'A/C#': [{ frets: ['x', 4, 2, 2, 2, 0] }],
  A7: [{ frets: ['x', 0, 2, 0, 2, 0] }],
  Asus4: [{ frets: ['x', 0, 2, 2, 3, 0] }],
  Am: [{ frets: ['x', 0, 2, 2, 1, 0] }],
  Am7: [{ frets: ['x', 0, 2, 0, 1, 0] }],
  Bb: [{ frets: ['x', 1, 3, 3, 3, 1], barre: { fret: 1, fromString: 5, toString: 1 } }],
  B7: [{ frets: ['x', 2, 1, 2, 0, 2] }],
  Bm: [{ frets: ['x', 2, 4, 4, 3, 2], barre: { fret: 2, fromString: 5, toString: 1 } }],
  Bm7: [{ frets: ['x', 2, 4, 2, 3, 2], barre: { fret: 2, fromString: 5, toString: 1 } }],
}

/** Intervalos (en semitonos desde la fundamental) de las calidades que reconoce el catálogo. */
const QUALITY_INTERVALS: Record<string, readonly number[]> = {
  '': [0, 4, 7],
  m: [0, 3, 7],
  '7': [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  add9: [0, 2, 4, 7],
  m7b5: [0, 3, 6, 10],
}

/** Clases de altura del acorde, o null si la calidad no está en la tabla. */
export function chordPitchClasses(chord: ParsedChord): Set<number> | null {
  const intervals = QUALITY_INTERVALS[chord.suffix]
  if (!intervals) return null
  const root = pitchClass(chord.root)
  const classes = new Set(intervals.map((interval) => (root + interval) % 12))
  if (chord.bass) classes.add(pitchClass(chord.bass))
  return classes
}

/** Notas que suenan en una digitación, de grave a agudo. */
export function voicingPitchClasses(voicing: Voicing): number[] {
  return voicing.frets.flatMap((fret, string) =>
    fret === 'x' ? [] : [(STANDARD_TUNING[string]! + fret) % 12],
  )
}

/** Clave de catálogo: notación americana sin símbolos tipográficos («Sib» → «Bb»). */
function catalogKey(chord: ParsedChord): string {
  return formatChord(chord, 'american')
}

/** Clave de un acorde en el catálogo (notación americana): sirve para recordar la digitación elegida. */
export function voicingKey(symbol: string): string | null {
  const parsed = parseChord(symbol)
  return parsed?.kind === 'chord' ? catalogKey(parsed) : null
}

export function catalogEntries(): [string, readonly Voicing[]][] {
  return Object.entries(CATALOG)
}

/**
 * Formas con cejilla que se mueven por el mástil: la fundamental va en la 6.ª cuerda (forma de Mi)
 * o en la 5.ª (forma de La). `null` es una cuerda que no se toca; los números son trastes por
 * encima del traste de la fundamental. Con el traste 0 son los acordes abiertos.
 */
interface Template {
  quality: string
  rootString: 6 | 5
  offsets: readonly (number | null)[]
  barre: boolean
}

const TEMPLATES: readonly Template[] = [
  { quality: '', rootString: 6, offsets: [0, 2, 2, 1, 0, 0], barre: true },
  { quality: 'm', rootString: 6, offsets: [0, 2, 2, 0, 0, 0], barre: true },
  { quality: '7', rootString: 6, offsets: [0, 2, 0, 1, 0, 0], barre: true },
  { quality: 'm7', rootString: 6, offsets: [0, 2, 0, 0, 0, 0], barre: true },
  { quality: 'maj7', rootString: 6, offsets: [0, 2, 1, 1, 0, 0], barre: true },
  { quality: 'sus4', rootString: 6, offsets: [0, 2, 2, 2, 0, 0], barre: true },
  { quality: '', rootString: 5, offsets: [null, 0, 2, 2, 2, 0], barre: true },
  { quality: 'm', rootString: 5, offsets: [null, 0, 2, 2, 1, 0], barre: true },
  { quality: '7', rootString: 5, offsets: [null, 0, 2, 0, 2, 0], barre: true },
  { quality: 'm7', rootString: 5, offsets: [null, 0, 2, 0, 1, 0], barre: true },
  { quality: 'maj7', rootString: 5, offsets: [null, 0, 2, 1, 2, 0], barre: true },
  { quality: 'sus4', rootString: 5, offsets: [null, 0, 2, 2, 3, 0], barre: true },
  { quality: 'sus2', rootString: 5, offsets: [null, 0, 2, 2, 0, 0], barre: true },
  { quality: 'm7b5', rootString: 5, offsets: [null, 0, 1, 0, 1, null], barre: false },
]

/** El traste más bajo que se pisa: ordena las formas de la posición más baja a la más alta. */
function position(voicing: Voicing): number {
  const pressed = voicing.frets.filter((fret): fret is number => fret !== 'x' && fret > 0)
  return pressed.length === 0 ? 0 : Math.min(...pressed)
}

function movableVoicings(chord: ParsedChord): Voicing[] {
  if (chord.bass) return []
  const root = pitchClass(chord.root)
  const found: Voicing[] = []
  for (const template of TEMPLATES) {
    if (template.quality !== chord.suffix) continue
    const open = STANDARD_TUNING[template.rootString === 6 ? 0 : 1]
    const fret = (((root - open) % 12) + 12) % 12
    const frets = template.offsets.map((offset) => (offset === null ? 'x' : offset + fret))
    const voicing: Voicing = { frets: frets as unknown as Voicing['frets'] }
    if (fret > 0 && template.barre) {
      voicing.barre = { fret, fromString: template.rootString, toString: 1 }
    }
    found.push(voicing)
  }
  return found.sort((a, b) => position(a) - position(b))
}

const sameFrets = (a: Voicing, b: Voicing) => a.frets.join(',') === b.frets.join(',')

const cache = new Map<string, readonly Voicing[]>()

/**
 * Digitaciones conocidas para un símbolo escrito en cualquier notación: primero las abiertas del
 * catálogo y después las formas con cejilla, de la posición más baja a la más alta.
 */
export function voicingsFor(symbol: string): readonly Voicing[] {
  const parsed = parseChord(symbol)
  if (!parsed || parsed.kind !== 'chord') return []
  const key = `${pitchClass(parsed.root)}|${parsed.suffix}|${parsed.bass ? pitchClass(parsed.bass) : ''}|${catalogKey(parsed)}`
  const cached = cache.get(key)
  if (cached) return cached
  const merged = [...(CATALOG[catalogKey(parsed)] ?? [])]
  for (const voicing of movableVoicings(parsed)) {
    if (!merged.some((existing) => sameFrets(existing, voicing))) merged.push(voicing)
  }
  cache.set(key, merged)
  return merged
}
